//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { createSelector } from 'reselect';

import {
  isFacebookPageFeedPostConfig,
  isInstagramFeedAutomaticPostConfig,
  isInstagramFeedManualPostConfig,
  isInstagramReelsAutomaticPostConfig,
  isInstagramReelsManualPostConfig,
  isInstagramStoryGroupAutomaticPostConfig,
  isInstagramStoryGroupPostConfig,
  isScheduledOrganicPinConfig,
  isTikTokPhotoAutomaticPostConfig,
  isTikTokPostConfig,
  isTwitterMediaPostConfig,
  isYouTubeAutomaticPostConfig,
} from '@apis/universal-posts/config/universalScheduledPostsConfigUtils';
import { IDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import {
  isFacebookPostAttemptFailure,
  isInstagramPostFailure,
  isPinterestPostAttemptFailure,
  isTikTokPostAttemptFailure,
  isTwitterPostAttemptFailure,
} from '@apis/universal-posts/scheduled-posts/postAttempts/universalScheduledPostAttemptsUtils';
import { IScheduledPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import {
  allUniversalPostingGroupNames,
  instagramStoryPostingGroupNames,
  manualPostingGroupNames,
} from '@apis/universal-posts/universalPostsApiModel';
import {
  getStandardPostDetails,
  isDraftPost,
} from '@apis/universal-posts/universalPostsUtils';
import { ICuralateStore } from '@models/AppState';
import DateString from '@models/DateString';
import PostableStatus from '@models/PostableStatus';
import { deserializeScheduledItemFailureStatus } from '@models/ScheduledItemFailureStatus';
import { SocialNetwork } from '@models/SocialNetwork';
import { getPostDetailsFailureStatusMessage as getPinterestPostDetailsFailureStatusMessage } from '@pinterest/components/post-details/pinterestPostDetailsUIConstants';
import { PinterestPostAttemptError } from '@pinterest/redux/post-details/pinterestPostDetailsModel';
import {
  ContentTypeFilter,
  PostStatusFilter,
  UniversalCalendarView,
  allPostStatuses,
  allScheduleNetworks,
  postedPostStatuses,
  scheduledPostStatuses,
} from '@universal-scheduling/redux/calendar/universalCalendarModel';
import {
  getFacebookPostDetailsFailureStatusMessage,
  getGenericPostTimeErrorMessage,
  getInstagramPostDetailsFailureStatusMessage,
  getOrderedMediaItems,
  getPostingGroupsForNetwork,
  getTikTokPostDetailsFailureStatusMessage,
  getTwitterPostDetailsFailureStatusMessage,
} from '@universal-scheduling/redux/calendar/universalCalendarUtils';
import { getYouTubePostTypeFromMedia } from '@universal-scheduling/utils/scheduler/schedulerSocialMediaNetworkUtils';
import { intersection } from '@utils/arrayUtils';
import { isDefined, isTruthy } from '@utils/filterPredicates';
import { getDateRangeTextFromDateStrings } from '@utils/social-scheduling/socialSchedulingSharedUtils';

export const getUniversalCalendar = (state: ICuralateStore) =>
  state.universalSchedule.universalCalendar;

export const getCurrentDateRange = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.dateRange,
);

export const getStartOfWeek = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.startOfWeek,
);

export const getScheduledPosts = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.scheduledPosts,
);

export const getDraftPosts = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.draftPosts,
);

export const getCurrentStartDate = createSelector(
  getCurrentDateRange,
  (dateRange) => dateRange.startDate,
);

export const getAvailableNetworks = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.availableNetworks,
);

export const getDisconnectedAvailableNetworks = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.disconnectedAvailableNetworks,
);

export const getSearchText = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.searchText,
);

export const getCurrentContentTypeFilter = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.contentTypeFilter,
);

export const getCurrentNetworkFilter = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.networkFilter,
);

export const getIsPostDetailsModalOpen = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.modals.isPostDetailsModalOpen,
);

export const getCurrentPostStatusFilter = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.postStatusFilter,
);

export const getCurrentPostingGroupsForRequest = createSelector(
  getAvailableNetworks,
  getCurrentNetworkFilter,
  getCurrentPostStatusFilter,
  getCurrentContentTypeFilter,
  (availableNetworks, networkFilter, postStatusFilter, contentTypeFilter) => {
    const availableNetworksPostingGroups = availableNetworks.flatMap(
      (network) => getPostingGroupsForNetwork(network),
    );

    const networkPostingGroups = getPostingGroupsForNetwork(networkFilter);

    const postStatusPostingGroups =
      postStatusFilter === PostStatusFilter.Manual
        ? manualPostingGroupNames
        : allUniversalPostingGroupNames;

    const contentTypePostingGroups =
      contentTypeFilter === ContentTypeFilter.Story
        ? instagramStoryPostingGroupNames
        : allUniversalPostingGroupNames;

    const postingGroupsForRequest = intersection(
      availableNetworksPostingGroups,
      intersection(
        intersection(networkPostingGroups, postStatusPostingGroups),
        contentTypePostingGroups,
      ),
    );
    return postingGroupsForRequest;
  },
);

export const getCurrentPostStatusesForRequest = createSelector(
  getCurrentPostStatusFilter,
  (postStatusFilter) => {
    switch (postStatusFilter) {
      case PostStatusFilter.Scheduled:
        return scheduledPostStatuses;
      case PostStatusFilter.Posted:
        return postedPostStatuses;
      default:
        return allPostStatuses;
    }
  },
);

export const getCurrentPostInModal = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.modals.currentPost,
);

export const getSchedulerCreatedPosts = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.modals.createdPosts,
);

export const getEditedPostInModal = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.modals.editedPost,
);

export const getEditedPostInModalMediaItems = createSelector(
  getEditedPostInModal,
  (post) => {
    if (post) {
      return getOrderedMediaItems(post);
    }
  },
);

export const getCurrentPostInModalHeaderText = createSelector(
  getCurrentPostInModal,
  getEditedPostInModalMediaItems,
  (currentPost, mediaItems) => {
    if (!isDefined(currentPost)) {
      return 'Post Details';
    }

    const { config } = currentPost;

    if (
      isFacebookPageFeedPostConfig(config) ||
      isInstagramFeedAutomaticPostConfig(config) ||
      isInstagramFeedManualPostConfig(config)
    ) {
      return 'Feed Post Details';
    } else if (
      isInstagramReelsAutomaticPostConfig(config) ||
      isInstagramReelsManualPostConfig(config)
    ) {
      return 'Reels Post Details';
    } else if (
      isInstagramStoryGroupAutomaticPostConfig(config) ||
      isInstagramStoryGroupPostConfig(config)
    ) {
      return 'Story Post Details';
    } else if (isScheduledOrganicPinConfig(config)) {
      return 'Pin Post Details';
    } else if (isTikTokPostConfig(config) || isTwitterMediaPostConfig(config)) {
      return 'Post Details';
    } else if (isYouTubeAutomaticPostConfig(config)) {
      return `YouTube ${getYouTubePostTypeFromMedia(mediaItems?.[0])} Details`;
    }

    return 'Post Details';
  },
);

export const getModalValidationErrors = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.modals.validationErrors,
);

export const getEditedScheduledTimeInModal = createSelector(
  getEditedPostInModal,
  (post) => post?.scheduledTime,
);

export const getEditedPostCaptionInModal = createSelector(
  getEditedPostInModal,
  (post) => {
    if (isDefined(post)) {
      return getStandardPostDetails(post).caption;
    }
  },
);

export const getEditablePinterestFieldsInModal = createSelector(
  getEditedPostInModal,
  (post) => {
    if (isDefined(post) && isScheduledOrganicPinConfig(post.config)) {
      return {
        boardId: post.config.boardId,
        sectionId: post.config.boardSection,
        title: post.config.title,
      };
    }
  },
);

export const getEditableTikTokPhotoTitleInModal = createSelector(
  getEditedPostInModal,
  (post) => {
    if (isDefined(post) && isTikTokPhotoAutomaticPostConfig(post.config)) {
      return post.config.title;
    }
  },
);

export const getYouTubeFieldsInModal = createSelector(
  getEditedPostInModal,
  (post) => {
    if (isDefined(post) && isYouTubeAutomaticPostConfig(post.config)) {
      return {
        title: post.config.title,
        description: post.config.description,
        privacy: post.config.privacy,
        playlistIds: post.config.playlistIds,
        tags: post.config.tags,
      };
    }
  },
);

export const getCurrentPostInModalMediaItems = createSelector(
  getCurrentPostInModal,
  (post) => {
    if (post) {
      return getOrderedMediaItems(post);
    }
  },
);

export const getCurrentPostInModalPreviewCuidIndex = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.modals.previewCuidIndex,
);

export const getPostFailureExplanation = (
  post: IScheduledPost | IDraftPost | undefined,
): string | undefined => {
  if (!isDefined(post) || isDraftPost(post)) {
    return undefined;
  }

  if (post.status !== PostableStatus.Failed) {
    return undefined;
  }

  const failure = post.failedAttempts?.[0];

  if (!isDefined(failure)) {
    return getGenericPostTimeErrorMessage(post.config.socialNetwork);
  }

  if (isInstagramPostFailure(failure)) {
    return getInstagramPostDetailsFailureStatusMessage(
      deserializeScheduledItemFailureStatus(failure.error),
    );
  }

  if (isPinterestPostAttemptFailure(failure)) {
    return getPinterestPostDetailsFailureStatusMessage(
      failure.error as PinterestPostAttemptError,
    );
  }

  if (isTikTokPostAttemptFailure(failure)) {
    return getTikTokPostDetailsFailureStatusMessage(failure);
  }

  if (isFacebookPostAttemptFailure(failure)) {
    return getFacebookPostDetailsFailureStatusMessage(failure);
  }

  if (isTwitterPostAttemptFailure(failure)) {
    return getTwitterPostDetailsFailureStatusMessage(
      failure.twitterPosterException,
    );
  }

  return getGenericPostTimeErrorMessage(post.config.socialNetwork);
};

export const getCurrentPostInModalPostFailureExplanation = createSelector(
  getCurrentPostInModal,
  getPostFailureExplanation,
);

export const getInstagramStoryGroupPostDetails = createSelector(
  getCurrentPostInModal,
  getCurrentPostInModalMediaItems,
  (modalPost, mediaItems) => {
    if (isDefined(modalPost) && isDefined(mediaItems)) {
      if (isInstagramStoryGroupPostConfig(modalPost.config)) {
        const {
          config: { storyClips },
        } = modalPost;

        const shortLinks = storyClips.reduce<string[]>((urls, clip) => {
          const url = clip.customizations.find(
            (customization) => customization.customizationType === 'SwipeUpUrl',
          )?.value;

          if (isTruthy(url)) {
            urls.push(url);
          }

          return urls;
        }, []);

        return { numClips: storyClips.length, shortLinks: shortLinks };
      } else if (isInstagramStoryGroupAutomaticPostConfig(modalPost.config)) {
        return {
          numClips: mediaItems.length,
        };
      }
    }
  },
);

export const getIsDeletePostModalOpen = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.modals.isDeletePostModalOpen,
);

export const getIsDeletingPost = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.flags.isDeletingPost,
);

export const getIsFetchingScheduledPosts = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.flags.isFetchingScheduledPosts,
);

export const getIsFetchingCalendarSettings = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.flags.isFetchingCalendarSettings,
);

export const getIsUpdatingPostDetails = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.flags.isUpdatingPostDetails,
);

export const getIsUpdatingPostScheduledTime = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.flags.isUpdatingPostScheduledTime,
);

export const getScheduleView = createSelector(
  getUniversalCalendar,
  (calendar) => calendar.scheduleView,
);

const getTimelineControlBarDateTextImpl = (startDate: DateString) => {
  const endDate = DateString.addDays(startDate, 6);
  return getDateRangeTextFromDateStrings(startDate, endDate);
};

export const getTimelineControlBarDateText = createSelector(
  getCurrentStartDate,
  getTimelineControlBarDateTextImpl,
);

export const getControlBarDateText = createSelector(
  getCurrentDateRange,
  getScheduleView,
  ({ endDate, startDate }, view) => {
    switch (view) {
      case UniversalCalendarView.Month:
        // We are guaranteed to be in the correct month by the end of the first week that
        // the month view is displaying on the calendar.
        return DateString.format(DateString.addDays(startDate, 6), 'MMMM YYYY');
      case UniversalCalendarView.Timeline:
        return getDateRangeTextFromDateStrings(startDate, endDate);
      case UniversalCalendarView.Week: {
        const includesMonthChange =
          DateString.parse(startDate).month !== DateString.parse(endDate).month;
        const includesYearChange =
          DateString.parse(startDate).year !== DateString.parse(endDate).year;

        if (includesYearChange) {
          return `${DateString.format(
            startDate,
            'MMM YYYY',
          )} - ${DateString.format(endDate, 'MMM YYYY')}`;
        } else if (includesMonthChange) {
          return `${DateString.format(startDate, 'MMM')} - ${DateString.format(
            endDate,
            'MMM YYYY',
          )}`;
        } else {
          return DateString.format(startDate, 'MMMM YYYY');
        }
      }
    }
  },
);

const requiredNetworksForPostStatus = {
  [PostStatusFilter.Draft]: allScheduleNetworks,
  [PostStatusFilter.Manual]: [SocialNetwork.Instagram],
  [PostStatusFilter.Posted]: allScheduleNetworks,
  [PostStatusFilter.Scheduled]: allScheduleNetworks,
};

export const getAvailableStatusFilters = createSelector(
  getAvailableNetworks,
  (availableNetworks) =>
    Object.values(PostStatusFilter).filter((status) =>
      availableNetworks.some((network) =>
        requiredNetworksForPostStatus[status].includes(network),
      ),
    ),
);

const possibleNetworksForContentType = {
  [ContentTypeFilter.Carousel]: [
    SocialNetwork.Instagram,
    SocialNetwork.Facebook,
    SocialNetwork.Pinterest,
    SocialNetwork.Twitter,
  ],
  [ContentTypeFilter.Photo]: [
    SocialNetwork.Instagram,
    SocialNetwork.Facebook,
    SocialNetwork.Pinterest,
    SocialNetwork.Twitter,
  ],
  [ContentTypeFilter.Story]: [SocialNetwork.Instagram],
  [ContentTypeFilter.Text]: [SocialNetwork.Facebook, SocialNetwork.Twitter],
  [ContentTypeFilter.Video]: allScheduleNetworks,
};

export const getAvailableContentTypes = createSelector(
  getAvailableNetworks,
  (availableNetworks) =>
    Object.values(ContentTypeFilter).filter((contentType) =>
      availableNetworks.some((network) =>
        possibleNetworksForContentType[contentType].includes(network),
      ),
    ),
);
